import {
    AfterViewInit,
    Directive,
    ElementRef,
    Injector,
    Input,
    booleanAttribute,
    effect,
    inject,
    isDevMode,
    untracked,
} from "@angular/core";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope as rootScope } from "@app/shared/i18n/scope";
import type { CerrixInputValidationErrorsDirective } from "./cerrix-input-validation-errors.directive";
import { CerrixInputValueSource } from "./models/cerrix-input-value-source.interface";
import { CerrixInputValidation } from "./models/cerrix-input-validation.interface";

/**
 * Marks a field as required. It goes on the input itself, or on a whole field component — a
 * picker, an input group — whose own template carries the `validationErrors` that reports for
 * the field. That directive reaches up, finds this one and hands it what it needs through
 * attachTo(), so both placements validate the same way.
 *
 * Until it is attached this directive validates nothing; it only states that the field is
 * required, which is what cerrix-label reads to draw its asterisk.
 */
@Directive({
    selector: "[appValidationRequired]",
    host: {
        "(input)": "onInput()",
        "(focusout)": "onBlur()",
    },
})
export class CerrixInputValidationRequiredDirective
    implements CerrixInputValidation, AfterViewInit
{
    // Defaults to true so the bare attribute marks a field required; without the transform it
    // would arrive as the empty string, which is falsy.
    @Input({ transform: booleanAttribute }) appValidationRequired = true;
    @Input() validationRequiredKey = "required";
    @Input() validationRequiredMessage: string | null = null;
    @Input() validationRequiredPriority = 10;

    private inputElement?: HTMLInputElement | HTMLTextAreaElement;

    // Both are handed over by attachTo(): they belong to the element that reports the error,
    // which is not this directive's own element when it sits on a field component.
    private errors: CerrixInputValidationErrorsDirective | null = null;
    private valueSource: CerrixInputValueSource | null = null;

    // Stays false until the field is left or the page validates itself, so a freshly
    // rendered form does not open with every required field already in error.
    private hasValidated = false;

    private elRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
    private injector = inject(Injector);

    // The English source text shows until the root scope is ready, so the message can never
    // render as its raw key.
    private defaultMessage = "This is a required field";

    constructor() {
        const translation = injectTranslation(rootScope);
        translation.ready().then(() => {
            this.defaultMessage = translation.t("validation.required");
        });
    }

    /**
     * Connects this directive to the errors directive that reports for the field, and to that
     * field's value when it keeps one. Called by the errors directive itself, so this directive
     * needs to know nothing about where it was placed.
     */
    attachTo(
        errors: CerrixInputValidationErrorsDirective,
        valueSource: CerrixInputValueSource | null,
    ) {
        // A field reports through one errors directive; a second offer is a nested field's.
        if (this.errors) return;

        this.errors = errors;
        this.valueSource = valueSource;
        this.inputElement = valueSource ? undefined : this.findInputElement();
        errors.register(this);

        if (!valueSource) return;

        valueSource.validationTouched?.subscribe(() => this.markTouched());

        // A field that keeps its value in a component (a picker) fires no (input) or (focusout)
        // when that value changes, so the value itself is watched to clear the error again.
        effect(
            () => {
                valueSource.validationValue();
                if (!this.hasValidated) return;

                // validate() both reads and writes the error map, and every write is a fresh Map
                // instance. Tracking those reads here would let this effect retrigger itself for
                // ever, so the value above is the only thing it watches.
                untracked(() => this.validate());
            },
            { injector: this.injector },
        );
    }

    ngAfterViewInit() {
        // A field component attaches this directive from inside its own view, whose hooks run
        // before these. Still unattached by now means nothing ever picked it up:
        // appValidationRequired on an element with no validationErrors on or inside it.
        if (!this.errors && isDevMode()) {
            const tag = this.elRef.nativeElement.tagName.toLowerCase();
            console.warn(
                `appValidationRequired on <${tag}> validates nothing: no validationErrors directive was found on or inside it.`,
            );
        }
    }

    private findInputElement(): HTMLInputElement | HTMLTextAreaElement {
        const native = this.elRef.nativeElement;
        if (native instanceof HTMLInputElement || native instanceof HTMLTextAreaElement) {
            return native;
        }
        return native.querySelector("input, textarea") as HTMLInputElement | HTMLTextAreaElement;
    }

    onInput() {
        if (this.valueSource?.validationTouched) return;
        this.validate();
    }

    onBlur() {
        // A field that is filled in elsewhere blurs the moment its dialog opens, which is the
        // middle of the interaction and not the end; it reports finishing through validationTouched.
        if (this.valueSource?.validationTouched) return;

        this.validate();
    }

    /** The field's own interaction finished, so from here on a missing value is reported. */
    private markTouched() {
        this.hasValidated = true;
        this.validate();
    }

    validate() {
        if (!this.errors || !this.appValidationRequired) return;

        // Only the DOM input can be missing, while its view has not rendered it yet. A value
        // source always has a value, and its null means nothing is filled in.
        if (!this.valueSource && !this.inputElement) return;

        const value = this.valueSource
            ? this.valueSource.validationValue()
            : this.inputElement.value;

        this.hasValidated = true;

        if (isEmpty(value)) {
            this.errors.add({
                key: this.validationRequiredKey,
                message: this.validationRequiredMessage ?? this.defaultMessage,
                priority: this.validationRequiredPriority,
            });
        } else {
            this.errors.remove(this.validationRequiredKey);
        }
    }
}

// A number of 0 and any object that is there count as filled in. A boolean is not handled yet:
// whether a required checkbox must be checked is decided once there is one.
function isEmpty(value: unknown): boolean {
    if (value == null) return true;
    if (typeof value === "string") return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "number") return Number.isNaN(value);
    if (value instanceof Date) return Number.isNaN(value.getTime());
    return false;
}
